import type { ZudokuConfig } from "zudoku";
import { Head } from "zudoku/components";
import React from "react";
import { LandingLayout } from "./src/components/LandingLayout.js";
import { SidebarAutoScroll } from "./src/components/SidebarAutoScroll.js";
import { SiteHomeLink } from "./src/components/SiteHomeLink.js";
import { SidebarHomeLink } from "./src/components/SidebarHomeLink.js";
import { ecommerceApiNav, llmApiNav } from "./src/generated/api-nav.js";
import {
  Hero,
  FeatureGrid,
  QuickStartBlock,
  EndpointShowcase,
} from "./src/components/landing/index.js";
import { brand, type BrandConfig } from "./src/brands/index.js";

function buildCustomCss(b: BrandConfig): string {
  const t = b.theme;

  // trenz 专属：打包的 Geist 字体（Variable woff2），仅 en 品牌加载
  const fontFaces =
    t.landingStyle === "minimal"
      ? `
      @font-face {
        font-family: "Geist";
        src: url("/fonts/Geist-Variable.woff2") format("woff2-variations");
        font-weight: 100 900;
        font-style: normal;
        font-display: swap;
      }
      @font-face {
        font-family: "Geist Mono";
        src: url("/fonts/GeistMono-Variable.woff2") format("woff2-variations");
        font-weight: 100 900;
        font-style: normal;
        font-display: swap;
      }
    `
      : "";

  // Landing 风格相关的 override：trenz 用 Stripe/Vercel 式文档排版
  const minimalOverrides =
    t.landingStyle === "minimal"
      ? `
      /* ───── Prose 技术文档质感覆盖 ───── */

      /* H1：超负字距、更大 margin */
      .prose h1 {
        font-weight: 700;
        letter-spacing: -0.04em;
        line-height: 1.1;
        font-size: clamp(32px, 3.6vw, 44px);
        margin-bottom: 28px;
      }

      /* H2：章节分割，底部 gradient hairline（从 border 色淡出到透明） */
      .prose h2 {
        margin-top: 56px;
        margin-bottom: 20px;
        padding-bottom: 12px;
        font-size: clamp(22px, 2.4vw, 28px);
        font-weight: 600;
        letter-spacing: -0.02em;
        background-image: linear-gradient(
          to right,
          var(--border) 0%,
          color-mix(in oklab, var(--border) 40%, transparent) 60%,
          transparent 100%
        );
        background-repeat: no-repeat;
        background-size: 100% 1px;
        background-position: bottom left;
      }

      /* H3：左侧 3px primary marker 竖条，Notion/Linear 风格 */
      .prose h3 {
        margin-top: 36px;
        margin-bottom: 10px;
        font-size: 17px;
        font-weight: 600;
        letter-spacing: -0.015em;
        position: relative;
        padding-left: 14px;
      }
      .prose h3::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0.35em;
        width: 3px;
        height: 0.85em;
        background: var(--brand-primary);
        border-radius: 2px;
      }

      /* 段落：更大字号 + 更宽松行高 + 略柔和字色 */
      .prose p {
        font-size: 16px;
        line-height: 1.75;
        color: color-mix(in oklab, var(--foreground) 84%, transparent);
        margin-top: 16px;
        margin-bottom: 16px;
      }

      /* 链接：无默认 underline，hover 时细线从下方出现 */
      .prose a:not(:where(h1, h2, h3, h4, h5, h6) a) {
        color: var(--brand-primary);
        text-decoration: none;
        border-bottom: 1px solid transparent;
        transition: border-color 160ms ease, color 160ms ease;
      }
      .prose a:not(:where(h1, h2, h3, h4, h5, h6) a):hover {
        border-bottom-color: color-mix(in oklab, var(--brand-primary) 50%, transparent);
      }

      /* Table：header uppercase + letter-spacing，row hover 微亮 */
      .prose table {
        margin-top: 24px;
        margin-bottom: 24px;
        border-collapse: separate;
        border-spacing: 0;
      }
      .prose table th {
        font-size: 11.5px;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--muted-foreground);
        background: color-mix(in oklab, var(--muted) 60%, transparent);
        padding: 12px 14px;
        text-align: left;
        border-bottom: 1px solid var(--border);
      }
      .prose table td {
        padding: 12px 14px;
        border-bottom: 1px solid var(--border);
        font-size: 14px;
        line-height: 1.6;
      }
      .prose table tbody tr {
        transition: background 120ms ease;
      }
      .prose table tbody tr:hover {
        background: color-mix(in oklab, var(--muted) 35%, transparent);
      }

      /* HR：gradient fade 边缘淡出 */
      .prose hr {
        border: none;
        height: 1px;
        background: linear-gradient(
          to right,
          transparent,
          var(--border) 50%,
          transparent
        );
        margin: 48px 0;
      }

      /* 列表：item 间距 + marker 色 */
      .prose ul > li,
      .prose ol > li {
        margin-top: 6px;
        margin-bottom: 6px;
      }
      .prose ul > li::marker,
      .prose ol > li::marker {
        color: var(--muted-foreground);
      }

      /* Strong 在文档页更精致（不是纯黑粗体） */
      .prose strong {
        font-weight: 600;
        color: var(--foreground);
      }
    `
      : `
      .prose h1 { font-weight: 700; letter-spacing: -0.02em; }
      .prose h2 { font-weight: 600; letter-spacing: -0.015em; }
      .prose h3 { font-weight: 600; letter-spacing: -0.01em; }
    `;

  return `
    ${fontFaces}

    :root {
      --font-sans: ${t.fontSans};
      --font-mono: ${t.fontMono};

      /* 品牌色变量（被 landing 组件直接引用）*/
      --brand-primary: ${t.brandPrimary};
      --brand-accent: ${t.brandAccent};
      --brand-accent-hover: ${t.brandAccent};
      --brand-gradient: ${t.brandGradient};
      --brand-gradient-soft: ${t.brandGradientSoft};

      /* 兼容旧变量名 — 出海匠 landing 组件历史上用的是 --brand-purple-solid */
      --brand-purple-solid: ${t.brandPrimary};
    }

    ${minimalOverrides}

    /* Blockquote / admonition：彩色左边 + 背景 tint，像 UI 组件。
       两个 brand 都启用，各自用自己的 --brand-primary 上色（chuhaijiang 紫 / trenz 蓝）。 */
    .prose blockquote,
    .prose :where([class*="admonition"], [class*="callout"]) {
      margin: 24px 0;
      padding: 14px 18px;
      border-radius: 10px;
      border-left: 3px solid var(--brand-primary);
      background: color-mix(in oklab, var(--brand-primary) 5%, var(--card));
      font-style: normal;
      color: var(--foreground);
    }
    .prose :where([class*="caution"], [class*="warning"]) {
      border-left-color: #f59e0b;
      background: color-mix(in oklab, #f59e0b 5%, var(--card));
    }

    /* 代码中去掉连字 */
    pre, code { font-variant-ligatures: none; }

    /* Inline code 不做字符级断行（zudoku 默认 wrap-anywhere 会把 async_on_complete 拆成
       async_on_comp/lete）。用 .prose code 提高特异性覆盖 .wrap-anywhere class。
       允许 cell 溢出，让表格自己处理横向滚动。*/
    .prose code.wrap-anywhere,
    .prose code:not(pre code) {
      white-space: nowrap !important;
      word-break: keep-all !important;
      overflow-wrap: normal !important;
    }
    /* 表格允许横向滚动，避免长 inline code 撑破布局 */
    .prose table {
      display: block;
      overflow-x: auto;
      max-width: 100%;
    }

    /* Anchor 滚动补偿 sticky header 高度（~115px + 16px 呼吸）*/
    :is(h1, h2, h3, h4, h5, h6)[id] {
      scroll-margin-top: 132px;
    }

    /* SPA fallback（点击 FeatureGrid / EndpointShowcase 卡片跳 /api/<tag>）加载时，
       dist/index.html 的 #root 是空壳：Layout mount 出一个 main wrapper（只含 loader）
       + footer。main wrapper 高度塌陷到 spinner 尺寸，footer 直接冒到视口顶部——
       用户看到的"先跳到底再跳转"就是这一帧。把 #root 改为 flex column 并让 footer
       margin-top: auto，保证空 main 状态下 footer 也被推到视口外。 */
    #root {
      display: flex !important;
      flex-direction: column !important;
      min-height: 100vh;
    }
    #root > footer {
      margin-top: auto !important;
    }

    /* pricing badge（zudoku patch 注入到 OperationListItem）配色：浅色用品牌色
       低饱和 tint，深色需要加深 tint 才能看清。这里用 CSS var 桥接 patch 的
       inline style，patch 里引用这些 var，dark 模式在下面 override。 */
    :root {
      --pricing-badge-bg: color-mix(in oklab, var(--brand-primary) 10%, transparent);
      --pricing-badge-border: color-mix(in oklab, var(--brand-primary) 30%, transparent);
      --pricing-badge-bg-default: color-mix(in oklab, var(--brand-primary) 18%, transparent);
      --pricing-badge-border-default: color-mix(in oklab, var(--brand-primary) 55%, transparent);
    }
    html.dark {
      --pricing-badge-bg: color-mix(in oklab, var(--brand-primary) 22%, var(--card));
      --pricing-badge-border: color-mix(in oklab, var(--brand-primary) 50%, transparent);
      --pricing-badge-bg-default: color-mix(in oklab, var(--brand-primary) 40%, var(--card));
      --pricing-badge-border-default: color-mix(in oklab, var(--brand-primary) 80%, transparent);
    }

    /* Landing 页 (/introduction) 去掉右侧 TOC 占位 */
    body.landing-page [data-pagefind-filter="section:markdown"] {
      grid-template-columns: 1fr !important;
    }
    body.landing-page [data-pagefind-filter="section:markdown"] > *:last-child:not(:first-child) {
      display: none !important;
    }
    body.landing-page [data-pagefind-filter="section:markdown"] > *:first-child {
      max-width: none !important;
      width: 100% !important;
    }

  `;
}

const LayoutAfterHead = () => (
  <>
    {/* trenz 专属：预加载 Geist woff2，消除 CSS parse 后再发起字体请求导致的 FOUT。
        React 19 会把 <link> 自动 hoist 到 <head>。*/}
    {brand.theme.landingStyle === "minimal" && (
      <Head>
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href="/fonts/Geist-Variable.woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href="/fonts/GeistMono-Variable.woff2"
          crossOrigin="anonymous"
        />
      </Head>
    )}
    <LandingLayout />
    <SidebarAutoScroll />
  </>
);

const navigation: ZudokuConfig["navigation"] = [
  "/introduction",
  {
    type: "category",
    label: brand.ecommerceCategoryLabel,
    icon: "shopping-bag",
    collapsed: false,
    items: [
      "/quick-start",
      "/billing",
      "/error-code",
      ...ecommerceApiNav,
    ],
  },
  ...(brand.hasLlmApi
    ? [
        {
          type: "category" as const,
          label: brand.llmCategoryLabel,
          icon: "bot",
          collapsed: false,
          items: [
            "/llm-introduction",
            "/llm-models",
            ...llmApiNav,
          ],
        },
      ]
    : []),
];

const apis: ZudokuConfig["apis"] = [
  {
    type: "url",
    input: "/openapi-spec",
    path: "/api",
    options: {
      expandAllTags: true,
    },
  },
  ...(brand.hasLlmApi
    ? [
        {
          type: "file" as const,
          input: "./apis/llm-openapi.json",
          path: "/llm-api",
          options: {
            expandAllTags: true,
          },
        },
      ]
    : []),
];

const config: ZudokuConfig = {
  metadata: {
    title: brand.metaTitleTemplate,
    description: brand.metaDescription,
    favicon: brand.favicon,
  },
  site: {
    title: brand.siteTitle,
    logo: {
      src: { light: brand.logoLight, dark: brand.logoDark },
      alt: brand.logoAlt,
      width: "140px",
      href: "/introduction",
    },
    showPoweredBy: false,
    footer: {
      copyright: brand.copyright,
      columns: brand.footerColumns,
    },
  },
  navigation,
  header: {
    placements: {
      navigation: "start",
    },
  },
  redirects: [{ from: "/", to: "/introduction" }],
  docs: {
    defaultOptions: {
      copyPage: false,
    },
  },
  apis,
  slots: {
    "layout-after-head": LayoutAfterHead,
    "head-navigation-start": SiteHomeLink,
    "navigation-before": SidebarHomeLink,
  },
  mdx: {
    components: {
      Hero,
      FeatureGrid,
      QuickStartBlock,
      EndpointShowcase,
    },
  },
  theme: {
    light: {
      primary: brand.theme.primaryLight,
      primaryForeground: brand.theme.primaryForegroundLight,
      ring: brand.theme.ringLight,
      radius: brand.theme.radius,
    },
    dark: {
      primary: brand.theme.primaryDark,
      primaryForeground: brand.theme.primaryForegroundDark,
      ring: brand.theme.ringDark,
      radius: brand.theme.radius,
    },
    customCss: buildCustomCss(brand),
  },
};

export default config;
